import type { ContactMessage } from '@/types';
import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAtom, useAtomValue } from 'jotai';
import { Button, Select, SelectItem, Tooltip, Chip } from '@heroui/react';
import { format } from 'date-fns';
import { RiEyeLine, RiMailLine, RiUserLine } from '@remixicon/react';

import { TablePage, TableFilters, FilterSelect, ColumnsToggleDropdown } from '@/components/table';
import { DateRangeFilter } from '@/components/shared/DateRangeFilter';
import { SearchInput } from '@/components/shared/SearchInput';
import { ContactMessageStatusBadge } from '@/components/contact-messages';
import { DateTimeDisplay } from '@/components/shared/DateTimeDisplay';
import {
  contactMessagesListParamsAtom,
  contactMessagesQueryAtom,
  contactSectionsAllAtom
} from '@/store';
import { useContactMessagesFilters } from '@/hooks/contact-messages';
import { useVisibleColumns } from '@/hooks/table';

const STATUS_FILTER_OPTIONS = [
  { key: 'all', label: 'All statuses' },
  { key: 'new', label: 'New' },
  { key: 'in_progress', label: 'In Progress' },
  { key: 'resolved', label: 'Resolved' },
  { key: 'archived', label: 'Archived' }
];

const formatDateRangeValue = (range: { start: Date | null; end: Date | null }) => {
  if (range.start && range.end) {
    return `${format(range.start, 'yyyy-MM-dd')},${format(range.end, 'yyyy-MM-dd')}`;
  }
  if (range.start) {
    return `${format(range.start, 'yyyy-MM-dd')},`;
  }
  if (range.end) {
    return `,${format(range.end, 'yyyy-MM-dd')}`;
  }
  return '';
};

const parseDateRangeValue = (value?: string) => {
  if (!value) {
    return { start: null, end: null };
  }
  const [startStr, endStr] = value.split(',');
  return {
    start: startStr ? new Date(startStr) : null,
    end: endStr ? new Date(endStr) : null
  };
};

export default function ContactMessagesPage() {
  const navigate = useNavigate();
  const sectionsQuery = useAtomValue(contactSectionsAllAtom);
  const { data, isLoading, isError, error } = useAtomValue(contactMessagesQueryAtom);
  const [, setParams] = useAtom(contactMessagesListParamsAtom);

  const sections = sectionsQuery?.data?.data ?? [];

  const {
    searchFields,
    setSearchFields,
    clearFilters,
    handlePageChange,
    hasAnyFilter,
    sortParam,
    setSortParam
  } = useContactMessagesFilters();

  // Set include for section relation (needed for section column display)
  React.useEffect(() => {
    setParams((prev) => {
      if (prev.include === 'section') return prev;
      return { ...prev, include: 'section' };
    });
  }, [setParams]);

  const [subjectQuery, setSubjectQuery] = React.useState(searchFields.subject || '');
  const [statusFilter, setStatusFilter] = React.useState(searchFields.status || 'all');
  const [sectionFilter, setSectionFilter] = React.useState(searchFields.section_id || 'all');
  const [createdRange, setCreatedRange] = React.useState<{ start: Date | null; end: Date | null }>(
    parseDateRangeValue(searchFields.created_between)
  );

  React.useEffect(() => {
    setSubjectQuery(searchFields.subject || '');
    setStatusFilter(searchFields.status || 'all');
    setSectionFilter(searchFields.section_id || 'all');
    setCreatedRange(parseDateRangeValue(searchFields.created_between));
  }, [searchFields.subject, searchFields.status, searchFields.section_id, searchFields.created_between]);

  const applyFilters = React.useCallback(
    (overrides?: {
      subject?: string;
      status?: string;
      section?: string;
      range?: { start: Date | null; end: Date | null };
    }) => {
      const nextSubject = overrides?.subject ?? subjectQuery;
      const nextStatus = overrides?.status ?? statusFilter;
      const nextSection = overrides?.section ?? sectionFilter;
      const nextRange = overrides?.range ?? createdRange;

      setSearchFields(() => ({
        subject: nextSubject,
        status: nextStatus === 'all' ? '' : nextStatus,
        section_id: nextSection === 'all' ? '' : nextSection,
        created_between: formatDateRangeValue(nextRange)
      }));
    },
    [createdRange, sectionFilter, setSearchFields, statusFilter, subjectQuery]
  );

  const handleClearFilters = React.useCallback(() => {
    setSubjectQuery('');
    setStatusFilter('all');
    setSectionFilter('all');
    setCreatedRange({ start: null, end: null });
    clearFilters();
  }, [clearFilters]);

  const handleSortChange = React.useCallback(
    (descriptor: SortDescriptor) => {
      const column = descriptor.column?.toString();
      if (!column) {
        setSortParam(null);
        return;
      }
      const prefix = descriptor.direction === 'descending' ? '-' : '';
      setSortParam(`${prefix}${column}`);
    },
    [setSortParam]
  );

  const [sortDescriptor, setSortDescriptor] = React.useState<SortDescriptor | undefined>(undefined);

  React.useEffect(() => {
    if (!sortParam) {
      setSortDescriptor(undefined);
      return;
    }
    const direction = sortParam.startsWith('-') ? 'descending' : 'ascending';
    const column = sortParam.replace(/^-/, '');
    setSortDescriptor((prev) => {
      if (prev?.column === column && prev?.direction === direction) {
        return prev;
      }
      return { column, direction } as SortDescriptor;
    });
  }, [sortParam]);

  const triggerRefresh = React.useCallback(() => {
    setParams((prev) => ({ ...prev, refresh: Date.now() }));
  }, [setParams]);

  const messages = React.useMemo<ContactMessage[]>(() => data?.data ?? [], [data]);

  const columns = React.useMemo(
    () => [
      { name: 'Subject', uid: 'subject', sortable: true },
      { name: 'Section', uid: 'section' },
      { name: 'Requester', uid: 'requester' },
      { name: 'Status', uid: 'status', sortable: true, align: 'center', headerAlign: 'center' },
      { name: 'Last Updated', uid: 'updated_at', sortable: true },
      { name: 'Actions', uid: 'actions', align: 'end' }
    ],
    []
  );

  const renderCell = React.useCallback(
    (message: ContactMessage, columnKey: React.Key) => {
      switch (columnKey) {
        case 'subject': {
          const isUnread = !message.read_at;
          return (
            <div className="flex flex-col gap-1">
              <button
                type="button"
                className={`text-left font-semibold transition-colors hover:text-primary ${isUnread ? 'text-primary' : ''}`}
                onClick={() => navigate(`/relationships/contact-messages/${message.id}`)}
              >
                {message.subject || '(No subject)'}
              </button>
              <p className="text-default-500 line-clamp-1 text-sm">{message.body}</p>
              {isUnread && <span className="text-success text-xs font-semibold">Unread</span>}
            </div>
          );
        }
        case 'section':
          return message.section ? (
            <Chip color="secondary" size="sm" variant="flat">
              {message.section.name}
            </Chip>
          ) : (
            <span className="text-default-400 text-sm">Unassigned</span>
          );
        case 'requester':
          return (
            <div className="flex flex-col gap-1 text-sm">
              <span className="flex items-center gap-1 font-medium">
                <RiUserLine className="text-default-400 h-4 w-4" />
                {message.name || 'Anonymous'}
              </span>
              {message.email && (
                <span className="flex items-center gap-1 text-default-500">
                  <RiMailLine className="text-default-400 h-4 w-4" />
                  {message.email}
                </span>
              )}
            </div>
          );
        case 'status':
          return (
            <div className="flex justify-center">
              <ContactMessageStatusBadge status={message.status} readAt={message.read_at} />
            </div>
          );
        case 'updated_at':
          return <DateTimeDisplay dateString={message.updated_at || message.created_at} />;
        case 'actions':
          return (
            <div className="flex justify-end">
              <Tooltip content="View details">
                <Button
                  isIconOnly
                  size="sm"
                  variant="light"
                  onPress={() => navigate(`/relationships/contact-messages/${message.id}`)}
                >
                  <RiEyeLine className="h-4 w-4" />
                </Button>
              </Tooltip>
            </div>
          );
        default:
          return null;
      }
    },
    [navigate]
  );

  const sectionOptions = React.useMemo(
    () => [
      { key: 'all', label: 'All sections' },
      ...sections.map((section) => ({ key: String(section.id), label: section.name }))
    ],
    [sections]
  );

  const { visibleColumns, setVisibleColumns, headerColumns } = useVisibleColumns(columns);

  return (
    <TablePage
      columns={columns}
      currentPage={data?.meta?.current_page}
      data={messages}
      emptyMessage="No contact messages found"
      error={error}
      errorMessage="Failed to load messages"
      filtersSection={
        <TableFilters
          hasActiveFilters={hasAnyFilter}
          isRefreshing={isLoading}
          onClearFilters={handleClearFilters}
          onRefresh={triggerRefresh}
        >
          <SearchInput
            value={subjectQuery}
            onValueChange={setSubjectQuery}
            onSearch={() => applyFilters()}
            onClear={() => {
              setSubjectQuery('');
              applyFilters({ subject: '' });
            }}
          />

          <ColumnsToggleDropdown
            allColumns={columns.map((c) => ({ uid: c.uid, name: c.name }))}
            selected={visibleColumns}
            onChange={setVisibleColumns}
          />

          <FilterSelect
            label="Status"
            options={STATUS_FILTER_OPTIONS}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value);
              applyFilters({ status: value });
            }}
          />

          <FilterSelect
            label="Section"
            value={sectionFilter}
            options={sectionOptions}
            onChange={(value) => {
              setSectionFilter(value);
              applyFilters({ section: value });
            }}
          />

          <DateRangeFilter
            label="Created between"
            value={createdRange}
            onChange={(value) => {
              setCreatedRange(value);
              applyFilters({ range: value });
            }}
            containerClass="min-w-[240px]"
          />
        </TableFilters>
      }
      headerColumns={headerColumns}
      isError={isError}
      isLoading={isLoading}
      renderCell={renderCell}
      sortDescriptor={sortDescriptor}
      title="Contact Messages"
      totalPages={data?.meta?.last_page}
      onPageChange={handlePageChange}
      onRefresh={triggerRefresh}
      onSortChange={(descriptor) => {
        setSortDescriptor(descriptor);
        handleSortChange(descriptor);
      }}
    />
  );
}

